import {
  BluetoothIosPrinterConfig,
  BluetoothIosPrintersConfig,
  PrinterDuty,
  PrinterTemplate,
} from '../types';
import { EscPosPrinter } from '@/plugins/escpos-printer';
import type { BluetoothEscPosDevice } from '@/plugins/escpos-printer';
import { encodeTemplateToEscPos, uint8ToBase64 } from '../escpos/encodeTemplateToEscPos';
import { normalizePaperWidth } from '../paperWidth';
import { readPrinterSetting, writePrinterSetting } from '../printerSettingsStorage';

const normalizeBluetoothIosPrinterConfig = (
  printer: Partial<BluetoothIosPrinterConfig>
): BluetoothIosPrinterConfig => {
  return {
    id: printer.id ?? crypto.randomUUID(),
    name: printer.name ?? 'iOS Bluetooth Printer',
    duties: printer.duties ?? ['cashier'],
    serialNumber: printer.serialNumber,
    accessoryName: printer.accessoryName,
    timeout: printer.timeout ?? 15000,
    codePage: printer.codePage ?? 'cp858',
    paperWidth: normalizePaperWidth(printer.paperWidth),
    openCashDrawer: printer.openCashDrawer ?? false,
    printDispatchCopy: printer.printDispatchCopy ?? false,
    language: printer.language ?? 'es',
    currency: printer.currency ?? 'USD',
  };
};

export const createDefaultBluetoothIosPrinterConfig = (
  overrides: Partial<BluetoothIosPrinterConfig> = {}
): BluetoothIosPrinterConfig => {
  return normalizeBluetoothIosPrinterConfig({
    id: crypto.randomUUID(),
    duties: ['cashier'],
    ...overrides,
  });
};

export const getBluetoothIosPrintersConfig = (): BluetoothIosPrintersConfig => {
  try {
    const dbConfig = readPrinterSetting<BluetoothIosPrintersConfig>('bluetoothIosPrintersConfig');
    if (dbConfig) {
      return {
        enabled: dbConfig.enabled ?? false,
        printers: (dbConfig.printers ?? []).map(normalizeBluetoothIosPrinterConfig),
      };
    }
  } catch (error) {
    console.warn('Failed to parse iOS Bluetooth printer config:', error);
  }

  return { enabled: false, printers: [] };
};

export const saveBluetoothIosPrintersConfig = async (
  config: BluetoothIosPrintersConfig
): Promise<void> => {
  await writePrinterSetting('bluetoothIosPrintersConfig', config);
};

const getPrintersForDuty = (duty: PrinterDuty): BluetoothIosPrinterConfig[] => {
  const config = getBluetoothIosPrintersConfig();
  if (!config.enabled || config.printers.length === 0) return [];
  return config.printers.filter((printer) => printer.duties.includes(duty));
};

export const listBluetoothIosDevices = async (): Promise<BluetoothEscPosDevice[]> => {
  const result = await EscPosPrinter.listBluetoothDevices();
  return result.devices ?? [];
};

export const showBluetoothIosPicker = async () => {
  return EscPosPrinter.showBluetoothPicker();
};

export const matchesBluetoothIosDevice = (
  printer: Pick<BluetoothIosPrinterConfig, 'serialNumber' | 'accessoryName'>,
  device: Pick<BluetoothEscPosDevice, 'serialNumber' | 'name'>
): boolean => {
  if (printer.serialNumber && device.serialNumber) {
    return printer.serialNumber === device.serialNumber;
  }
  if (printer.accessoryName && device.name) {
    return printer.accessoryName === device.name;
  }
  return false;
};

export const sendToBluetoothIosPrinter = async (
  template: PrinterTemplate,
  printerId: string,
  options?: { openCashDrawer?: boolean },
): Promise<boolean> => {
  const config = getBluetoothIosPrintersConfig();
  if (!config.enabled) return false;

  const printer = config.printers.find((item) => item.id === printerId);
  if (!printer) {
    throw new Error(`iOS Bluetooth printer not found: ${printerId}`);
  }

  const bytes = encodeTemplateToEscPos(template, {
    openCashDrawer: options?.openCashDrawer ?? printer.openCashDrawer,
    codePage: printer.codePage,
  });

  await EscPosPrinter.printRawBluetooth({
    dataBase64: uint8ToBase64(bytes),
    serialNumber: printer.serialNumber,
    accessoryName: printer.accessoryName,
    timeoutMs: printer.timeout,
  });
  return true;
};

export const sendToBluetoothIosPrinterByDuty = async (
  template: PrinterTemplate,
  duty: PrinterDuty
): Promise<boolean> => {
  const printers = getPrintersForDuty(duty);
  if (printers.length === 0) {
    console.warn(`No iOS Bluetooth printer assigned for duty: ${duty}`);
    return false;
  }

  const results = await Promise.all(
    printers.map((printer) => {
      console.log(`[iOS Bluetooth] Sending document to printer: ${printer.name} (Duty: ${duty})`);
      return sendToBluetoothIosPrinter(template, printer.id);
    })
  );

  return results.some((success) => success);
};
